import { t } from '@/i18n';
import {
  DateRangeSchema,
  DayjsSchema,
  requiredStringType,
  sortOrderType,
} from '@/utils';
import { implementationMethodEnum, projectCategoryEnum } from '@/utils/enum';
import { z } from 'zod';
import {
  FundAdditionalAttributesModel,
  InspectionEvaluationKsFormSchema,
  LegalBasisModel,
  PlanModel,
  PoliciesForProjectModel,
  SubsidyRateModel,
  projectRelationType,
  reasonForNotSettingEndPeriod,
  skipAdministrativeReformTeamEvaluationReason,
} from '../../_models/project';

/// //////////////////////////////////////////////////
/// Common
/// /////////////////////////////////////////////////

const FormBaseModel = z.object({
  fiscalYear: DayjsSchema(t('validation.required')),
  oldProjectNumber: z.string().max(30).nullish(),
  ministryId: z.string().uuid(),
  name: z.string().min(1),
  organizationId: z.string().uuid(),
  independentAdministrativeAgencyId: z.string().uuid().nullable(),
  relatedOrganizationIds: z.string().uuid().array(),
  peopleInChargeIds: z.string().uuid().array().min(1),
  businessManagerIds: z.string().uuid().array().min(1),
  isUnknownStart: z.boolean().default(false),
  isNoScheduledEnd: z.boolean().default(false),
  category: projectCategoryEnum.nullish(), // nullable only for KS
  legalBases: z.preprocess(
    // TODO: Replace the empty fallback array with proper handling for NULL values in the backend.
    // This is a temporary workaround.
    (data) => data ?? [],
    LegalBasisModel.array().nullish(),
  ),
  plans: PlanModel.array().nullish(),
  purpose: z.string().nullish(),
  task: z.string().nullish(),
  overview: z.string().nullish(),
  overviewUrl: z.string().url().nullish(),
  subsidyRates: SubsidyRateModel.array().nullish(),
  remark: z.string().nullish(),
  sortOrder: sortOrderType.max(9999).nullish(),
  nestedOtherResponsibleOrganizationIds: z
    .string()
    .uuid()
    .array()
    .array()
    .optional(),
  fiscalYearOfSnapshot: z.string().array().nullish(),
  period: DateRangeSchema.nullish(),
  relatedProjects: z
    .object({
      id: requiredStringType.uuid(),
      relationType: projectRelationType,
    })
    .array()
    .nullish(),
});

/// //////////////////////////////////////////////////
/// RS
/// /////////////////////////////////////////////////

const policiesForProjectFormSchema = z.object({
  policyId: z.string().uuid().array().min(1),
  policyUrl: z.preprocess(
    (data) => data || null,
    PoliciesForProjectModel.shape.policyUrl,
  ),
});

const GeneralRsFormSchema = FormBaseModel.pick({
  oldProjectNumber: true,
  ministryId: true,
  name: true,
  isUnknownStart: true,
  isNoScheduledEnd: true,
  peopleInChargeIds: true,
  category: true,
  legalBases: true,
  plans: true,
  subsidyRates: true,
  remark: true,
  sortOrder: true,
  businessManagerIds: true,
  fiscalYearOfSnapshot: true,
  fiscalYear: true,
  period: true,
  nestedOtherResponsibleOrganizationIds: true,
  relatedProjects: true,
}).extend({
  category: FormBaseModel.shape.category.unwrap(),
  nestedRelatedOrganizationIds: FormBaseModel.shape.relatedOrganizationIds
    .min(1)
    .array()
    .min(1),
  organizationIds: FormBaseModel.shape.organizationId.array(),
  policiesForProject: policiesForProjectFormSchema.array().min(1),
  mainCostIds: z.string().uuid().array().min(1),
  purpose: z.string().min(1),
  task: z.string().min(1),
  overview: z.string().min(1),
  overviewUrl: z.preprocess(
    (data) => data || null,
    FormBaseModel.shape.overviewUrl,
  ),
  implementationMethods: implementationMethodEnum.array().min(1),
});

const SkippedReviewRsFormSchema = FormBaseModel.pick({
  ministryId: true,
  oldProjectNumber: true,
  name: true,
  peopleInChargeIds: true,
  businessManagerIds: true,
  remark: true,
  isUnknownStart: true,
  isNoScheduledEnd: true,
  sortOrder: true,
  fiscalYearOfSnapshot: true,
  fiscalYear: true,
  period: true,
  nestedOtherResponsibleOrganizationIds: true,
}).extend({
  category: FormBaseModel.shape.category.unwrap(),
  nestedRelatedOrganizationIds: FormBaseModel.shape.relatedOrganizationIds
    .array()
    .optional(),
  majorExpenditureBudgetPurposeIds: z.string().array().nullish(),
  organizationIds: FormBaseModel.shape.organizationId.array(),
  policiesForProject: policiesForProjectFormSchema.array().nullish(),
  skipAdministrativeReformTeamEvaluationReason,
  skipAdministrativeReformTeamEvaluationReasonSupplement: z.preprocess(
    (data) => data || null,
    z.string().nullish(),
  ),
});

/**
 * use object with data as key because
 * discriminated has this problem.
 * https://github.com/colinhacks/zod/issues/1884
 */
export const RsFormSchema = z.object({
  data: z.discriminatedUnion('skipAdministrativeReformTeamEvaluation', [
    GeneralRsFormSchema.extend({
      skipAdministrativeReformTeamEvaluation: z.literal(false),
    }),
    SkippedReviewRsFormSchema.extend({
      skipAdministrativeReformTeamEvaluation: z.literal(true),
    }),
  ]),
});

/// //////////////////////////////////////////////////
/// SS
/// /////////////////////////////////////////////////

/**
 * use object with data as key because
 * discriminated has this problem.
 * https://github.com/colinhacks/zod/issues/1884
 */
export const SsFormSchema = z.object({
  data: FormBaseModel.pick({
    oldProjectNumber: true,
    ministryId: true,
    name: true,
    peopleInChargeIds: true,
    legalBases: true,
    plans: true,
    remark: true,
    sortOrder: true,
    businessManagerIds: true,
    isUnknownStart: true,
    isNoScheduledEnd: true,
    fiscalYearOfSnapshot: true,
    fiscalYear: true,
    period: true,
    nestedOtherResponsibleOrganizationIds: true,
    relatedProjects: true,
  }).extend({
    category: FormBaseModel.shape.category.unwrap(),
    nestedRelatedOrganizationIds: FormBaseModel.shape.relatedOrganizationIds
      .array()
      .optional(),
    independentAdministrativeAgencyIds:
      FormBaseModel.shape.independentAdministrativeAgencyId.unwrap().array(),
    organizationIds: FormBaseModel.shape.organizationId.array().min(1),
    mainCostIds: z.string().uuid().array().min(1),
    purpose: z.string().min(1),
    task: z.string().min(1),
    overview: z.string().min(1),
    overviewUrl: z.preprocess(
      (data) => data || undefined,
      FormBaseModel.shape.overviewUrl,
    ),
    segmentUnitConcept: z.string().min(1),
  }),
});

/// //////////////////////////////////////////////////
/// KS
/// /////////////////////////////////////////////////

/**
 * use object with data as key because
 * discriminated has this problem.
 * https://github.com/colinhacks/zod/issues/1884
 */
export const KsFormSchema = z.object({
  data: FormBaseModel.pick({
    oldProjectNumber: true,
    ministryId: true,
    name: true,
    peopleInChargeIds: true,
    category: true,
    legalBases: true,
    plans: true,
    purpose: true,
    task: true,
    overview: true,
    overviewUrl: true,
    remark: true,
    businessManagerIds: true,
    isUnknownStart: true,
    isNoScheduledEnd: true,
    fiscalYearOfSnapshot: true,
    fiscalYear: true,
    period: true,
    nestedOtherResponsibleOrganizationIds: true,
    relatedProjects: true,
  }).extend({
    nestedRelatedOrganizationIds: FormBaseModel.shape.relatedOrganizationIds
      .min(1)
      .array()
      .min(1),
    organizationIds: FormBaseModel.shape.organizationId.array().min(1),
    fundAdditionalAttributes: FundAdditionalAttributesModel.pick({
      fundSheetNumber: true,
      fundSheetBranchNumber: true,
      fundSheetType: true,
      corporateForm: true,
      fundBusinessName: true,
      effortAndResult: true,
      necessityType: true,
      necessityReasons: true,
      fundOwner: true,
      optionalCorporateForm: true,
      operationForms: true,
      businessForms: true,
    })
      .extend({
        additionalJurisdictionalUsers: z
          .object({
            ministryId: z.string().uuid(),
            businessManagerIds: z.string().uuid().array().min(1),
            peopleInChargeIds: z.string().uuid().array().min(1),
            organizationIds: z.string().uuid().array().min(1),
          })
          .array()
          .nullish(),
        scheduledEnd: z
          .object({
            endDate: DayjsSchema().nullish(),
            reasonsForNotSettingScheduledEndPeriod:
              reasonForNotSettingEndPeriod.nullish(), // 2025年度以降の事業では使わない
            supplementaryReasonsForEndPeriod: z.string().nullish(),
            reasonsForChangingScheduledEndPeriod: z.string().nullish(),
            endDateOfNewApplicationReceiving: DayjsSchema().nullish(),
            reasonsForNotSettingEndPeriodOfNewApplicationReceiving:
              reasonForNotSettingEndPeriod.nullish(), // 2025年度以降の事業では使わない
            supplementaryReasonsForNewApplicationReceiving: z
              .string()
              .nullish(),
            reasonsForChangingEndPeriodOfNewApplicationReceiving: z
              .string()
              .nullish(),
          })
          .nullish(),
      })
      .nullish(),
    overviewUrl: z.preprocess(
      (data) => data || null,
      FormBaseModel.shape.overviewUrl,
    ),
    inspectionEvaluation:
      InspectionEvaluationKsFormSchema.deepPartial().nullish(),
  }),
});

/// //////////////////////////////////////////////////
/// Form Schema
/// /////////////////////////////////////////////////

export const ProjectFormSchema = z.discriminatedUnion('sheetType', [
  RsFormSchema.extend({ sheetType: z.literal('RS') }),
  SsFormSchema.extend({ sheetType: z.literal('SS') }),
  KsFormSchema.extend({ sheetType: z.literal('KS') }),
]);

export const PartialProjectFormSchema = z.discriminatedUnion('sheetType', [
  RsFormSchema.deepPartial().extend({ sheetType: z.literal('RS') }),
  SsFormSchema.deepPartial().extend({ sheetType: z.literal('SS') }),
  KsFormSchema.deepPartial().extend({ sheetType: z.literal('KS') }),
]);
